/**
 * JobBoardOverview - Mini kanban overview for the dashboard
 * Shows job counts by status using DB-driven workflow configuration
 */

import { Link } from 'react-router-dom';
import {
  Clock,
  LogIn,
  Search,
  FileSearch,
  FileClock,
  Wrench,
  PackageSearch,
  ClipboardCheck,
  Car,
  CheckCircle2,
  CheckCircle,
  Archive,
  XCircle,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { useJobStatusCounts } from '../../../hooks/useJobs';
import { useJobWorkflowByType } from '../../../hooks/useWorkflowConfig';
import { cn } from '@/lib/utils';

/** Map icon names from workflow config to Lucide components */
const ICON_MAP: Record<string, LucideIcon> = {
  'clock': Clock,
  'log-in': LogIn,
  'search': Search,
  'file-search': FileSearch,
  'file-clock': FileClock,
  'wrench': Wrench,
  'package-search': PackageSearch,
  'clipboard-check': ClipboardCheck,
  'check-circle': CheckCircle,
  'car': Car,
  'check-circle-2': CheckCircle2,
  'archive': Archive,
  'x-circle': XCircle,
};

function getIcon(iconName?: string): LucideIcon {
  if (!iconName) return Wrench;
  return ICON_MAP[iconName] || Wrench;
}

function StatusPill({
  icon: Icon,
  count,
  status,
  textColor,
  bgColor,
}: {
  icon: LucideIcon;
  count: number;
  status: string;
  textColor: string;
  bgColor: string;
}) {
  return (
    <Link
      to={`/jobs?status=${status}`}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-md transition-all',
        'hover:ring-2 hover:ring-offset-1 hover:ring-gray-300',
        count === 0 && 'opacity-50'
      )}
      style={{ backgroundColor: bgColor }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color: textColor }} />
      <span className="text-xs font-medium" style={{ color: textColor }}>
        {count}
      </span>
    </Link>
  );
}

export function JobBoardOverview() {
  const { counts, isLoading, refetch } = useJobStatusCounts();
  // Fetch the job workflow (fallback/default - uses private as base)
  const { data: jobWorkflow } = useJobWorkflowByType('private' as const);

  // Build status list from workflow config, filtering to jobboard-visible statuses
  const jobStatuses = (jobWorkflow?.statuses ?? [])
    .filter((s) => s.showInJobboard && s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const totalActive = jobStatuses.reduce(
    (sum, s) => sum + (counts[s.code] || 0),
    0
  );
  const completedCount = counts['COMPLETED'] || 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-gray-400" />
            <h3 className="text-xs font-medium text-gray-900">
              Trabajos
            </h3>
            <span className="text-xs text-gray-500">
              ({totalActive} activos)
            </span>
          </div>
          <Link
            to="/jobs"
            className="text-[10px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
          >
            Ver tablero
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Status Pills */}
      <div className="p-3">
        {isLoading ? (
          <div className="flex flex-wrap gap-2">
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className="h-6 w-16 bg-gray-100 rounded-md animate-pulse"
              />
            ))}
          </div>
        ) : jobStatuses.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {jobStatuses.map((status) => (
              <StatusPill
                key={status.code}
                icon={getIcon(status.icon)}
                count={counts[status.code] || 0}
                status={status.code}
                textColor={status.color}
                bgColor={status.bgColor}
              />
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500 text-center py-2">
            Sin flujo de trabajos configurado
          </div>
        )}
      </div>

      {/* Footer with completed count */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/50 rounded-b-lg">
        <div className="flex items-center justify-between text-[10px]">
          <div className="flex items-center gap-1.5 text-gray-500">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            <span>
              {completedCount} completados este mes
            </span>
          </div>
          <button
            onClick={() => refetch()}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            Actualizar
          </button>
        </div>
      </div>
    </div>
  );
}

export function JobBoardOverviewSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="p-3">
        <div className="flex flex-wrap gap-2">
          {[...Array(9)].map((_, i) => (
            <div
              key={i}
              className="h-6 w-16 bg-gray-100 rounded-md animate-pulse"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
